import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FeaturedPropertiesCarousel = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [savedProperties, setSavedProperties] = useState(new Set());

  const featuredProperties = [
    {
      id: 1,
      title: "Luxury 3BHK Apartment",
      location: "Koramangala, Bangalore",
      price: "₹45,000",
      type: "rent",
      bhk: "3 BHK",
      area: "1,200 sq ft",
      image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&h=600&fit=crop",
      features: ["Furnished", "Parking", "Gym", "Swimming Pool"],
      verified: true,
    },
    {
      id: 2,
      title: "Modern 2BHK Villa",
      location: "Bandra West, Mumbai",
      price: "₹1.2 Cr",
      type: "buy",
      bhk: "2 BHK",
      area: "950 sq ft",
      image: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&h=600&fit=crop",
      features: ["Semi-Furnished", "Balcony", "Security", "Garden"],
      verified: true,
    },
    {
      id: 3,
      title: "Spacious 4BHK Penthouse",
      location: "Connaught Place, Delhi",
      price: "₹85,000",
      type: "rent",
      bhk: "4 BHK",
      area: "2,100 sq ft",
      image: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&h=600&fit=crop",
      features: ["Fully Furnished", "Terrace", "Lift", "Power Backup"],
      verified: true,
    },
    {
      id: 4,
      title: "Cozy 1BHK Studio",
      location: "Anna Nagar, Chennai",
      price: "₹18,000",
      type: "rent",
      bhk: "1 BHK",
      area: "600 sq ft",
      image: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&h=600&fit=crop",
      features: ["Furnished", "AC", "WiFi", "Kitchen"],
      verified: true,
    },
    {
      id: 5,
      title: "Premium 3BHK Flat",
      location: "Hitech City, Hyderabad",
      price: "₹95 Lakh",
      type: "buy",
      bhk: "3 BHK",
      area: "1,450 sq ft",
      image: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800&h=600&fit=crop",
      features: ["Under Construction", "Clubhouse", "Parking", "Lift"],
      verified: true,
    },
    {
      id: 6,
      title: "Heritage 2BHK Apartment",
      location: "Salt Lake, Kolkata",
      price: "₹28,000",
      type: "rent",
      bhk: "2 BHK",
      area: "850 sq ft",
      image: "https://images.unsplash.com/photo-1484154218962-a197022b5858?w=800&h=600&fit=crop",
      features: ["Semi-Furnished", "Balcony", "Market Nearby", "Transport"],
      verified: true,
    }
  ];

  const itemsPerSlide = 3;
  const totalSlides = Math.ceil(featuredProperties.length / itemsPerSlide);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const toggleSave = (propertyId) => {
    setSavedProperties(prev => {
      const newSet = new Set(prev);
      if (newSet.has(propertyId)) {
        newSet.delete(propertyId);
      } else {
        newSet.add(propertyId);
      }
      return newSet;
    });
  };

  const handlePropertyClick = (property) => {
    navigate('/property-detail-page', { state: { property } });
  };

  const getCurrentSlideProperties = () => {
    const startIndex = currentSlide * itemsPerSlide;
    return featuredProperties.slice(startIndex, startIndex + itemsPerSlide);
  };

  return (
    <section className="py-16 bg-canvas">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <h2 className="text-3xl font-bold text-text-primary mb-2">
              Featured Properties
            </h2>
            <p className="text-text-secondary">
              Handpicked properties with verified details and premium features
            </p>
          </div>
          
          <div className="hidden md:flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              iconName="ArrowLeft"
              onClick={prevSlide}
              disabled={currentSlide === 0}
            />
            <span className="text-sm text-text-secondary">
              {currentSlide + 1} of {totalSlides}
            </span>
            <Button
              variant="outline"
              size="sm"
              iconName="ArrowRight"
              onClick={nextSlide}
              disabled={currentSlide === totalSlides - 1}
            />
          </div>
        </div>

        {/* Properties Grid */}
        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {getCurrentSlideProperties().map((property) => (
              <div
                key={property.id}
                className="bg-surface rounded-xl shadow-property-card border border-border overflow-hidden hover:shadow-modal transition-smooth cursor-pointer group"
                onClick={() => handlePropertyClick(property)}
              >
                {/* Property Image */}
                <div className="relative h-48 overflow-hidden">
                  <Image
                    src={property.image}
                    alt={property.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-smooth"
                  />
                  
                  {/* Save Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSave(property.id);
                    }}
                    className="absolute top-3 right-3 w-8 h-8 bg-surface/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-surface transition-quick"
                  >
                    <Icon
                      name="Heart"
                      size={16}
                      className={savedProperties.has(property.id) ? 'text-error fill-current' : 'text-text-secondary'}
                    />
                  </button>

                  {/* Property Type Badge */}
                  <div className="absolute top-3 left-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      property.type === 'rent' ?'bg-secondary text-secondary-foreground' :'bg-primary text-primary-foreground'
                    }`}>
                      For {property.type === 'rent' ? 'Rent' : 'Sale'}
                    </span>
                  </div>

                  {/* Verification Badge */}
                  {property.verified && (
                    <div className="absolute bottom-3 left-3">
                      <div className="flex items-center space-x-1 bg-success/90 backdrop-blur-sm text-success-foreground px-2 py-1 rounded-full text-xs">
                        <Icon name="CheckCircle" size={12} />
                        <span>Verified</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Property Details */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-text-primary mb-1 group-hover:text-primary transition-quick">
                        {property.title}
                      </h3>
                      <div className="flex items-center text-text-secondary text-sm">
                        <Icon name="MapPin" size={14} className="mr-1" />
                        {property.location}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-primary">
                        {property.price}
                      </div>
                      <div className="text-xs text-text-secondary">
                        {property.type === 'rent' ? '/month' : 'Total'}
                      </div>
                    </div>
                  </div>

                  {/* Property Info */}
                  <div className="flex items-center justify-between text-sm text-text-secondary mb-4">
                    <span className="flex items-center">
                      <Icon name="Home" size={14} className="mr-1" />
                      {property.bhk}
                    </span>
                    <span className="flex items-center">
                      <Icon name="Maximize" size={14} className="mr-1" />
                      {property.area}
                    </span>
                    <span className="flex items-center">
                      <Icon name="Clock" size={14} className="mr-1" />
                      {property.postedDate}
                    </span>
                  </div>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {property.features.slice(0, 3).map((feature, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-muted text-text-secondary text-xs rounded-md"
                      >
                        {feature}
                      </span>
                    ))}
                    {property.features.length > 3 && (
                      <span className="px-2 py-1 bg-muted text-text-secondary text-xs rounded-md">
                        +{property.features.length - 3} more
                      </span>
                    )}
                  </div>

                  {/* Recent Activity */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-text-secondary">
                      <Icon name="Eye" size={12} className="mr-1" />
                      {property.recentInquiries} recent inquiries
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="ArrowRight"
                      iconPosition="right"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePropertyClick(property);
                      }}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Navigation */}
          <div className="flex md:hidden items-center justify-center space-x-4 mt-8">
            <Button
              variant="outline"
              size="sm"
              iconName="ArrowLeft"
              onClick={prevSlide}
              disabled={currentSlide === 0}
            />
            <span className="text-sm text-text-secondary">
              {currentSlide + 1} of {totalSlides}
            </span>
            <Button
              variant="outline"
              size="sm"
              iconName="ArrowRight"
              onClick={nextSlide}
              disabled={currentSlide === totalSlides - 1}
            />
          </div>
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Button
            variant="outline"
            size="lg"
            iconName="Grid3X3"
            iconPosition="left"
            onClick={() => navigate('/property-search-discovery')}
          >
            View All Properties
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedPropertiesCarousel;